// @ts-nocheck
import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { filter, get } from 'lodash'
import dayjs from 'dayjs'

import { colors } from '@lmc/common/constants'
import { showToast } from '../../../../common/utils'
import { HelpLink } from '../../../generic'
import {
  EmarMedicationAllDetails,
  EmarMedicationResidentInfo,
  EmarMedicationTitle
} from '../components'
import MARChart from '../../../../screens/Residents/screens/ResidentEmarScreens/EMARChart/EMARChart'
import { Button, Icon } from '@lmc/ui-kit'

/**
@description Renders the final step in the Add eMar Medication modal.

@param {object} fields. The form fields.

@param {object} formState. The form state. Stores the medication name.

@param {object} resident. The resident.

@returns {function} AddMedSaveStep.
*/
interface AddMedSaveStepProps {
  fields: Record<string, any>
  formState: Record<string, any>
  resident: Record<string, any>
  mixpanel?: any // Add appropriate type
  createItem: (params: Record<string, any>) => void
  fetchData: () => void
}

interface AddMedSaveStepState {
  isSubmitting: boolean
}

export default class AddMedSaveStep extends Component<
  AddMedSaveStepProps,
  AddMedSaveStepState
> {
  state: AddMedSaveStepState = {
    isSubmitting: false
  }

  stepRef: React.RefObject<HTMLDivElement> | null = null

  constructor(props: AddMedSaveStepProps) {
    super(props)
    this.stepRef = React.createRef()
  }

  componentDidMount() {
    const { mixpanel } = this.props
    if (mixpanel) mixpanel.track('CO_emar_modal_review_step')
    if (this.stepRef.current) {
      this.stepRef.current.scrollIntoView()
    }
  }

  handleSave = () => {
    const { createItem, fetchData, fields, mixpanel } = this.props
    const body = {
      ...fields,
      endDateComment: fields.hasEndDate_comment,
      startDate:
        fields.startDate && dayjs(fields.startDate, 'DD-MM-YYYY').toISOString(),
      endDate:
        fields.endDate && dayjs(fields.endDate, 'DD-MM-YYYY').toISOString()
    }

    if (body.PRN) {
      body.emarRoundsDetails = []
      const dose = get(fields, 'emarRoundsDetails.0.dose')
      const altDose = get(fields, 'emarRoundsDetails.0.altDose')
      if (dose) body.dose = dose
      if (altDose) body.altDose = altDose
      // Note: null values will be rejected by the backend
    } else {
      body.emarRoundsDetails = filter(fields.emarRoundsDetails, 'isChecked')
    }

    this.setState({
      isSubmitting: true
    })

    createItem({
      json: body,
      url: 'list/emar-resident-medications',
      onComplete: async () => {
        fetchData()
        showToast({ title: 'Added a Medication' })
        if (mixpanel) mixpanel.track('CO_emar_modal_completed')
      },
      onError: (e: Error) => {
        console.log(e)
        showToast({ type: 'error', title: 'Something went wrong...' })
        this.setState({ isSubmitting: false })
      }
    })
  }

  // Render functions

  renderResidentInfo = () => {
    const { resident } = this.props
    return <EmarMedicationResidentInfo resident={resident} />
  }

  renderMedicationTitle = () => {
    const {
      formState: { medicationName }
    } = this.props
    return <EmarMedicationTitle medicationName={medicationName} />
  }

  renderMARChart = () => {
    const {
      fields,
      formState: { medicationName },
      testId
    } = this.props

    const chartMedication = {
      ...fields,
      roundTimings: filter(fields.emarRoundsDetails, 'isChecked'),
      name: medicationName
    }

    const startDate = dayjs(fields.startDate, 'DD-MM-YYYY')

    const endDate = fields.hasEndDate
      ? dayjs(fields.endDate, 'DD-MM-YYYY')
      : dayjs(startDate).add(1, 'years')

    return (
      <div
        style={styles.marChartContainer}
        data-testid={testId ? `${testId}-save-mar-chart` : 'save-mar-chart'}
      >
        <MARChart
          displayLegend={false}
          displayTableHeader={false}
          medications={[chartMedication]}
          startDate={startDate}
          endDate={endDate}
        />
        <img alt="" src={LABEL_URL} style={styles.arrowImage} />
      </div>
    )
  }

  renderMedicationDetailsSection = () => {
    const { fields, formState, testId } = this.props

    return (
      <div
        style={styles.detailsContainer}
        data-testid={testId ? `${testId}-save-details` : 'save-details'}
      >
        <EmarMedicationAllDetails fields={fields} formState={formState} />
      </div>
    )
  }

  renderFooter = () => {
    const { testId } = this.props
    return (
      <div
        style={styles.footer}
        data-testid={testId ? `${testId}-save-footer` : 'save-footer'}
      >
        <HelpLink supportLink="https://support.logmycare.co.uk/en/articles/4851795-how-do-i-add-a-medication" />
        <Button
          disabled={this.state.isSubmitting}
          loading={this.state.isSubmitting}
          onClick={() => this.handleSave()}
          variant="primary"
          icon={<Icon name="CheckOutlined" />}
          data-testid={
            testId ? `${testId}-save-medication-btn` : 'save-medication-btn'
          }
        >
          Save Medication
        </Button>
      </div>
    )
  }

  render() {
    const { testId } = this.props
    return (
      <div
        style={styles.container}
        data-testid={
          testId ? `${testId}-add-med-save-step` : 'add-med-save-step'
        }
      >
        <div ref={this.stepRef} style={styles.topSpacing} />
        {/* {this.renderResidentInfo()} */}
        {/* {this.renderMedicationTitle()} */}
        {this.renderMARChart()}
        {this.renderMedicationDetailsSection()}
        {this.renderFooter()}
      </div>
    )
  }
}

const LABEL_URL =
  'https://lmc-data-production.s3.eu-west-2.amazonaws.com/icons/emar/mar-chart-label.png'

const styles = {
  arrowContainer: {
    position: 'relative',
    top: '-225px',
    left: '100%',
    marginLeft: '16px'
  },
  arrowImage: {
    width: 140,
    position: 'relative',
    top: '-225px',
    left: '100%',
    marginLeft: '16px'
  },
  container: {
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'column',
    margin: 'auto',
    maxWidth: 960,
    padding: '0px 10px'
  },
  detailContainer: {
    borderBottom: `1px solid ${colors.grey}`,
    padding: '5px 0px'
  },
  detailInfo: {
    lineHeight: '22px'
  },
  detailTitle: {
    fontWeight: 700,
    lineHeight: '22px'
  },
  detailsContainer: {
    marginBottom: 32,
    width: '100%'
  },
  detailsListContainer: {
    borderRadius: 15,
    padding: '5px 10px 12px'
  },
  detailsTitle: {
    fontWeight: 400,
    fontSize: 20,
    lineHeight: '30px',
    marginBottom: 3
  },
  detailsTitleContainer: {
    marginBottom: 16
  },
  detailsTitleUnderline: {
    border: `1px solid ${colors.grey}`
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%'
  },
  marChartContainer: {
    margin: '28px 0px -70px',
    width: '100%'
  },
  topSpacing: {
    height: 225
  }
}
